import { MessageType } from '@wooorms/shared';
import type { InputState } from '@wooorms/shared';
import type { NetworkManager } from './NetworkManager';
import type { Camera } from '../game/Camera';
import { MobileDeviceManager } from '../utils/MobileDetection';
import { MobileSettings } from '../utils/MobileSettings';

/**
 * Mobile input state from external controls (joystick/buttons)
 */
export interface MobileInputState {
  /** Whether mobile controls are providing input */
  active: boolean;
  /** Joystick angle in radians (if using joystick mode) */
  joystickAngle: number;
  /** Joystick force (0-1, for deadzone handling) */
  joystickForce: number;
  /** Whether sprint button is pressed */
  sprinting: boolean;
  /** Whether crawl button is pressed */
  crawling: boolean;
}

/**
 * Configuration for InputManager
 */
export interface InputManagerConfig {
  /** Rate at which to send inputs to server (Hz) */
  sendRate: number;
  /** Minimum angle change (radians) to trigger send */
  angleThreshold: number;
  /** Canvas element to attach listeners to */
  canvas: HTMLCanvasElement;
  /** Whether to enable mobile controls */
  enableMobileControls?: boolean;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: Partial<InputManagerConfig> = {
  sendRate: 20, // 20 Hz = 50ms between sends
  angleThreshold: 0.01, // ~0.6 degrees
};

/**
 * InputManager - Captures and sends player input to the server
 *
 * Handles:
 * - Mouse/touch position tracking
 * - Target angle calculation from cursor position
 * - Boost state from click/space
 * - Rate-limited input sending to server
 */
export class InputManager {
  /** Network manager for sending input */
  private networkManager: NetworkManager | null = null;

  /** Camera for coordinate conversion */
  private camera: Camera;

  /** Configuration */
  private config: InputManagerConfig;

  /** Current mouse/touch position (screen coordinates) */
  private cursorScreenPos: { x: number; y: number } = { x: 0, y: 0 };

  /** Current target angle (radians) */
  private targetAngle: number = 0;

  /** Whether boost is currently active */
  private boosting: boolean = false;

  /** Input sequence number for reconciliation */
  private sequenceNumber: number = 0;

  /** Last sent input state (for change detection) */
  private lastSentInput: { angle: number; boosting: boolean } | null = null;

  /** Interval for sending input */
  private sendInterval: ReturnType<typeof setInterval> | null = null;

  /** Whether input handling is active */
  private active: boolean = false;

  /** Player head position (for angle calculation) */
  private playerHeadPos: { x: number; y: number } = { x: 0, y: 0 };

  /** Mobile input state (from external mobile controls) */
  private mobileInput: MobileInputState = {
    active: false,
    joystickAngle: 0,
    joystickForce: 0,
    sprinting: false,
    crawling: false,
  };

  /** Whether mobile controls are enabled */
  private mobileControlsEnabled: boolean = false;

  /** Whether crawl mode is active */
  private crawling: boolean = false;

  /** Bound event handlers (for cleanup) */
  private boundHandlers: {
    mouseMove: (e: MouseEvent) => void;
    mouseDown: (e: MouseEvent) => void;
    mouseUp: (e: MouseEvent) => void;
    mouseLeave: () => void;
    touchStart: (e: TouchEvent) => void;
    touchMove: (e: TouchEvent) => void;
    touchEnd: (e: TouchEvent) => void;
    keyDown: (e: KeyboardEvent) => void;
    keyUp: (e: KeyboardEvent) => void;
  };

  constructor(camera: Camera, config: Partial<InputManagerConfig>) {
    if (!config.canvas) {
      throw new Error('Canvas element is required');
    }

    this.camera = camera;
    this.config = { ...DEFAULT_CONFIG, ...config } as InputManagerConfig;

    // Check if mobile controls should be enabled
    const deviceManager = MobileDeviceManager.getInstance();
    const settings = MobileSettings.getInstance();
    this.mobileControlsEnabled = config.enableMobileControls ??
      (deviceManager.hasTouch && settings.mobileControlsEnabled);

    // Bind event handlers
    this.boundHandlers = {
      mouseMove: this.handleMouseMove.bind(this),
      mouseDown: this.handleMouseDown.bind(this),
      mouseUp: this.handleMouseUp.bind(this),
      mouseLeave: this.handleMouseLeave.bind(this),
      touchStart: this.handleTouchStart.bind(this),
      touchMove: this.handleTouchMove.bind(this),
      touchEnd: this.handleTouchEnd.bind(this),
      keyDown: this.handleKeyDown.bind(this),
      keyUp: this.handleKeyUp.bind(this),
    };
  }

  /**
   * Start capturing and sending input
   */
  start(networkManager: NetworkManager): void {
    if (this.active) {
      this.stop();
    }

    this.networkManager = networkManager;
    this.active = true;
    this.sequenceNumber = 0;
    this.lastSentInput = null;

    // Initialize cursor position to screen center
    // This ensures we have a valid angle even before the first mouse move
    const canvas = this.config.canvas;
    this.cursorScreenPos.x = canvas.width / 2;
    this.cursorScreenPos.y = canvas.height / 2;

    // Calculate initial angle from cursor position
    // The worm will point towards screen center initially
    const worldPos = this.camera.screenToWorld(this.cursorScreenPos.x, this.cursorScreenPos.y);
    const dx = worldPos.x - this.playerHeadPos.x;
    const dy = worldPos.y - this.playerHeadPos.y;
    this.targetAngle = Math.atan2(dy, dx);

    console.log('[InputManager] Started', {
      connected: networkManager.isConnected(),
      cursorScreen: this.cursorScreenPos,
      worldPos,
      playerHeadPos: this.playerHeadPos,
      initialAngle: this.targetAngle,
    });

    // Attach event listeners
    this.attachListeners();

    // Start send interval
    const sendIntervalMs = 1000 / this.config.sendRate;
    this.sendInterval = setInterval(() => {
      this.sendInput();
    }, sendIntervalMs);
  }

  /**
   * Stop capturing and sending input
   */
  stop(): void {
    this.active = false;
    this.networkManager = null;

    // Remove event listeners
    this.detachListeners();

    // Stop send interval
    if (this.sendInterval) {
      clearInterval(this.sendInterval);
      this.sendInterval = null;
    }
  }

  /**
   * Attach event listeners to canvas
   */
  private attachListeners(): void {
    const canvas = this.config.canvas;

    // Mouse events
    canvas.addEventListener('mousemove', this.boundHandlers.mouseMove);
    canvas.addEventListener('mousedown', this.boundHandlers.mouseDown);
    canvas.addEventListener('mouseup', this.boundHandlers.mouseUp);
    canvas.addEventListener('mouseleave', this.boundHandlers.mouseLeave);

    // Touch events
    canvas.addEventListener('touchstart', this.boundHandlers.touchStart, { passive: false });
    canvas.addEventListener('touchmove', this.boundHandlers.touchMove, { passive: false });
    canvas.addEventListener('touchend', this.boundHandlers.touchEnd, { passive: false });

    // Keyboard events (on window for space key)
    window.addEventListener('keydown', this.boundHandlers.keyDown);
    window.addEventListener('keyup', this.boundHandlers.keyUp);
  }

  /**
   * Detach event listeners from canvas
   */
  private detachListeners(): void {
    const canvas = this.config.canvas;

    // Mouse events
    canvas.removeEventListener('mousemove', this.boundHandlers.mouseMove);
    canvas.removeEventListener('mousedown', this.boundHandlers.mouseDown);
    canvas.removeEventListener('mouseup', this.boundHandlers.mouseUp);
    canvas.removeEventListener('mouseleave', this.boundHandlers.mouseLeave);

    // Touch events
    canvas.removeEventListener('touchstart', this.boundHandlers.touchStart);
    canvas.removeEventListener('touchmove', this.boundHandlers.touchMove);
    canvas.removeEventListener('touchend', this.boundHandlers.touchEnd);

    // Keyboard events
    window.removeEventListener('keydown', this.boundHandlers.keyDown);
    window.removeEventListener('keyup', this.boundHandlers.keyUp);
  }

  /**
   * Handle mouse move
   */
  private handleMouseMove(e: MouseEvent): void {
    this.updateCursorPosition(e.clientX, e.clientY);
  }

  /**
   * Handle mouse down
   */
  private handleMouseDown(e: MouseEvent): void {
    if (e.button === 0) { // Left click
      this.boosting = true;
    }
  }

  /**
   * Handle mouse up
   */
  private handleMouseUp(e: MouseEvent): void {
    if (e.button === 0) {
      this.boosting = false;
    }
  }

  /**
   * Handle mouse leave
   */
  private handleMouseLeave(): void {
    this.boosting = false;
  }

  /**
   * Handle touch start
   * When mobile controls are enabled, this is handled by VirtualJoystick
   */
  private handleTouchStart(e: TouchEvent): void {
    // Skip if mobile controls are handling touch input
    if (this.mobileControlsEnabled) return;

    e.preventDefault();
    const touch = e.touches[0];
    if (touch) {
      this.updateCursorPosition(touch.clientX, touch.clientY);
      this.boosting = true;
    }
  }

  /**
   * Handle touch move
   * When mobile controls are enabled, this is handled by VirtualJoystick
   */
  private handleTouchMove(e: TouchEvent): void {
    // Skip if mobile controls are handling touch input
    if (this.mobileControlsEnabled) return;

    e.preventDefault();
    const touch = e.touches[0];
    if (touch) {
      this.updateCursorPosition(touch.clientX, touch.clientY);
    }
  }

  /**
   * Handle touch end
   * When mobile controls are enabled, this is handled by VirtualJoystick
   */
  private handleTouchEnd(e: TouchEvent): void {
    // Skip if mobile controls are handling touch input
    if (this.mobileControlsEnabled) return;

    e.preventDefault();
    this.boosting = false;
  }

  /**
   * Handle key down
   */
  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      this.boosting = true;
    }
    if (e.key === 'Shift') {
      this.crawling = true;
    }
  }

  /**
   * Handle key up
   */
  private handleKeyUp(e: KeyboardEvent): void {
    if (e.key === ' ' || e.key === 'Spacebar') {
      this.boosting = false;
    }
    if (e.key === 'Shift') {
      this.crawling = false;
    }
  }

  /**
   * Update cursor position and calculate target angle
   */
  private updateCursorPosition(screenX: number, screenY: number): void {
    this.cursorScreenPos.x = screenX;
    this.cursorScreenPos.y = screenY;

    // Convert to world coordinates
    const worldPos = this.camera.screenToWorld(screenX, screenY);

    // Calculate angle from player head to cursor
    const dx = worldPos.x - this.playerHeadPos.x;
    const dy = worldPos.y - this.playerHeadPos.y;
    this.targetAngle = Math.atan2(dy, dx);
  }

  /**
   * Update the player's head position (called by game loop)
   */
  updatePlayerPosition(x: number, y: number): void {
    this.playerHeadPos.x = x;
    this.playerHeadPos.y = y;

    // Recalculate angle with new head position
    const worldPos = this.camera.screenToWorld(this.cursorScreenPos.x, this.cursorScreenPos.y);
    const dx = worldPos.x - x;
    const dy = worldPos.y - y;
    this.targetAngle = Math.atan2(dy, dx);
  }

  /**
   * Send input to server
   */
  private sendInput(): void {
    if (!this.active || !this.networkManager || !this.networkManager.isConnected()) {
      if (this.sequenceNumber < 5) {
        console.log('[InputManager] Cannot send - not connected', {
          active: this.active,
          hasNetworkManager: !!this.networkManager,
          isConnected: this.networkManager?.isConnected(),
        });
      }
      return;
    }

    // Check if input has changed significantly
    const angleChanged = this.lastSentInput === null ||
      Math.abs(this.targetAngle - this.lastSentInput.angle) > this.config.angleThreshold;
    const boostChanged = this.lastSentInput === null ||
      this.boosting !== this.lastSentInput.boosting;

    // Only send if something changed or periodically for keep-alive
    if (!angleChanged && !boostChanged && this.sequenceNumber % 10 !== 0) {
      return;
    }

    const input: InputState = {
      targetAngle: this.targetAngle,
      boosting: this.boosting,
      timestamp: Date.now(),
      sequence: this.sequenceNumber++,
    };

    // Log first few inputs
    if (this.sequenceNumber <= 5) {
      console.log('[InputManager] Sending input', input);
    }

    this.networkManager.send(MessageType.INPUT, input);

    // Update last sent state
    this.lastSentInput = {
      angle: this.targetAngle,
      boosting: this.boosting,
    };
  }

  /**
   * Get current target angle
   */
  getTargetAngle(): number {
    return this.targetAngle;
  }

  /**
   * Get current boost state
   */
  isBoosting(): boolean {
    return this.boosting;
  }

  /**
   * Get cursor position in world coordinates
   */
  getCursorWorldPos(): { x: number; y: number } {
    return this.camera.screenToWorld(this.cursorScreenPos.x, this.cursorScreenPos.y);
  }

  /**
   * Get cursor position in screen coordinates
   */
  getCursorScreenPos(): { x: number; y: number } {
    return { ...this.cursorScreenPos };
  }

  /**
   * Manually trigger a boost state change
   */
  setBoost(boosting: boolean): void {
    this.boosting = boosting;
  }

  /**
   * Check if input manager is active
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * Check if crawl mode is active
   */
  isCrawling(): boolean {
    // Mobile crawl takes priority
    if (this.mobileInput.active && this.mobileInput.crawling) {
      return true;
    }
    return this.crawling;
  }

  /**
   * Set crawl state
   */
  setCrawl(crawling: boolean): void {
    this.crawling = crawling;
  }

  /**
   * Check if mobile controls are enabled
   */
  hasMobileControls(): boolean {
    return this.mobileControlsEnabled;
  }

  /**
   * Enable/disable mobile controls
   */
  setMobileControlsEnabled(enabled: boolean): void {
    this.mobileControlsEnabled = enabled;
  }

  /**
   * Update mobile input state from external controls (VirtualJoystick, MobileActionButtons)
   */
  setMobileInput(input: Partial<MobileInputState>): void {
    Object.assign(this.mobileInput, input);

    // Update boosting state from mobile sprint button
    if (input.sprinting !== undefined) {
      this.boosting = input.sprinting;
    }

    // Update crawling state from mobile crawl button
    if (input.crawling !== undefined) {
      this.crawling = input.crawling;
    }

    // Update target angle from joystick
    const settings = MobileSettings.getInstance();
    if (input.joystickAngle !== undefined && input.joystickForce !== undefined) {
      if (settings.isJoystickMode && input.joystickForce > 0) {
        this.targetAngle = input.joystickAngle;
      }
    }
  }

  /**
   * Get mobile input state
   */
  getMobileInput(): MobileInputState {
    return { ...this.mobileInput };
  }

  /**
   * Get effective boost state (considering mobile input)
   */
  getEffectiveBoosting(): boolean {
    if (this.mobileInput.active && this.mobileInput.sprinting) {
      return true;
    }
    return this.boosting;
  }

  /**
   * Get effective crawl state (considering mobile input)
   */
  getEffectiveCrawling(): boolean {
    if (this.mobileInput.active && this.mobileInput.crawling) {
      return true;
    }
    return this.crawling;
  }

  /**
   * Get effective target angle (considering mobile joystick)
   */
  getEffectiveTargetAngle(): number {
    const settings = MobileSettings.getInstance();

    // In joystick mode with active input, use joystick angle
    if (this.mobileInput.active &&
        settings.isJoystickMode &&
        this.mobileInput.joystickForce > 0) {
      return this.mobileInput.joystickAngle;
    }

    // Otherwise use cursor-based angle
    return this.targetAngle;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stop();
  }
}
